import { Injectable, Inject } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { of } from 'rxjs/observable/of';
import { catchError } from 'rxjs/operators/catchError';
import { mergeMap } from 'rxjs/operators/mergeMap';
import { EMPTY } from 'rxjs/internal/observable/empty';
import get from 'lodash-es/get';

import { LoadingService } from '@finder/shared/services/loading-service/loading-service.service';
import { CAMPAIGN_CONSTANTS } from '@finder/shared/constants/app.constants';
import { FinderApiConnectorService } from '@finder/shared/services/finder-api-connector/finder-api-connector.service';
import { FinderThemeService } from '@finder/shared/services/theme-service/finder-theme.service';
import { FinderAnalyticsService } from '@finder/shared/services/finder-analytics/finder-analytics.service';
import { SoloService } from '@finder/shared/services/solo-service/solo.service';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { LoggerService } from '@wdpr/ra-angular-logger';
import { FinderPageKeyService } from '@finder/shared/services/finder-page-key/finder-page-key.service';

@Injectable()
export class DynamicResolver implements Resolve<any> {

    constructor(
        private loadingSvc: LoadingService,
        private finderApi: FinderApiConnectorService,
        private themeService: FinderThemeService,
        private finderAnalyticsService: FinderAnalyticsService,
        private soloService: SoloService,
        private windowRef: WindowRef,
        private logger: LoggerService,
        private pageKeyService: FinderPageKeyService,
        @Inject(DOCUMENT) private document: Document,
    ) { }

    resolve(route: ActivatedRouteSnapshot) {
        // When coming from listing page we need to make sure we scroll top
        // and we set loading state into false
        this.document.documentElement.scrollTop = 0;
        // Reset of Error state to avoid getting Error message stuck after back button pressed GIT-31197
        this.loadingSvc.setErrorState(null);
        this.loadingSvc.setLoadingState(false);

        const slugId = route.paramMap.get('id');

        return this.finderApi.getDetailsBySlugId(slugId)
            .pipe(
                catchError(error => {
                    // Get the status code if available, or just use 400 as default
                    const status = get(error, 'status', '400');

                    // check if this page is gated by myid
                    if (status === 401 && get(error, 'error.myIdGated', false)) {
                        // Redirect to myid login
                        this.windowRef.nativeWindow.location.href =
                            this.soloService.getLoginRedirectUrl(this.windowRef.nativeWindow.location.href);
                        return EMPTY;
                    }

                    if (status === 403 && get(error, 'error.myIdGated', false)) {
                        const message = `THIS PAGE IS GATED: The current MyId user doesn't have access to this page`;
                        error.error.myIdGatedMessage = message;
                        this.logger.warn(message);
                    }

                    // Analytics for error page
                    this.finderAnalyticsService.updateErrorPage(status);
                    this.finderAnalyticsService.updateStoreType();
                    this.finderAnalyticsService.updateModel();

                    this.loadingSvc.setErrorState(error);
                    this.loadingSvc.setLoadingState(true);
                    return EMPTY;
                }),
                mergeMap(data => {
                    // Check to make sure this is CT data
                    const isCampaign = get(data, CAMPAIGN_CONSTANTS.isCampaign, false);
                    if (isCampaign) {
                        this.changeTheme(data);
                    }

                    // Set fallback value for pagekey service
                    this.pageKeyService.setFallbackPageKey(data.webLink);

                    // Add a class to hook CSS onto to remove elements of the global nav and
                    // footer, per https://jira-nge.disney.com/browse/GIT-8790
                    const useMinimal = get(data, 'useMinimal', false);

                    if (useMinimal) {
                        this.addBodyClass('minimal-syndicated');
                    }

                    this.loadingSvc.setLoadingState(true);
                    return of(data);
                })
            );
    }

    private changeTheme(data) {
        // Get the theme - there should ALWAYS be a theme property
        const theme = get(data, CAMPAIGN_CONSTANTS.themeKey);

        // Set the theme
        this.themeService.setCurrentTheme(theme.campaign);

        // Check if we need to change the nav to the dark theme
        if (theme.syndicated) {
            this.addBodyClass(theme.syndicated);
        }

        // Check if this is an overview
        const isOverview = get(data, CAMPAIGN_CONSTANTS.isOverview, false);
        if (isOverview) {
            this.addBodyClass('overview');
        }
    }

    private addBodyClass(bodyClass) {
        this.document.body.classList.add(bodyClass);
    }
}
